// Faz BP-polish (2026-05-13) — Şifremi unuttum sayfası (admin paneli)
// Backend: POST /api/auth/forgot-password — email gönderir, cevap her zaman
// aynı (kullanıcı varlığı sızdırmaz). Burada UI sadece "gönderildi" mesajı
// gösterir; gerçek mail Brevo üzerinden gider.

import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, TextField, Button, Typography, Alert, Stack, Link } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { forgotPassword } from '../services/api';

const Logo = ({ size = 64 }) => (
  <Box
    aria-label="Miksan Plastik"
    sx={{
      width: size, height: size, borderRadius: '50%',
      bgcolor: '#FFFFFF',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      boxShadow: '0 4px 18px rgba(15,42,79,0.30)',
      mx: 'auto'
    }}
  >
    <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <text x="50" y="55" textAnchor="middle" dominantBaseline="middle"
            fontFamily="Inter, system-ui, sans-serif" fontWeight="800" fontSize="42" fill="#1E4FA0">
        MK
      </text>
    </svg>
  </Box>
);

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await forgotPassword(email);
      setSent(true);
    } catch {
      // Backend zaten her durumda 200 dönüyor (timing-safe). Yine de network
      // hatasi vs. icin defansif catch — tek senaryosu DNS/offline.
      setError('Bağlantı hatası. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0F2A4F 0%, #1E4FA0 100%)',
        px: 2,
        py: 4,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(15,42,79,0.18), 0 2px 8px rgba(15,42,79,0.10)',
        }}
        elevation={0}
      >
        <Stack spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Logo size={72} />
          <Box textAlign="center">
            <Typography variant="h5" fontWeight={700} sx={{ color: 'text.primary', mb: 0.5 }}>
              Şifrenizi mi unuttunuz?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              E-posta adresinizi girin, sıfırlama linki gönderelim
            </Typography>
          </Box>
        </Stack>

        {sent ? (
          <Stack spacing={2}>
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              E-posta adresiniz sistemimizde kayıtlıysa, sıfırlama linki gönderildi.
              Lütfen gelen kutunuzu (ve spam klasörünü) kontrol edin.
            </Alert>
            <Button
              fullWidth
              variant="outlined"
              component={RouterLink}
              to="/login"
              size="large"
              startIcon={<ArrowBackIcon />}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Giriş sayfasına dön
            </Button>
          </Stack>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

            <TextField
              fullWidth
              label="E-posta"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={e => setEmail(e.target.value)}
              sx={{ mb: 2.5 }}
              required
            />

            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={loading}
              size="large"
              sx={{ py: 1.4, fontWeight: 700, textTransform: 'none', fontSize: 15, borderRadius: 2, mb: 1.5 }}
            >
              {loading ? 'Gönderiliyor...' : 'Sıfırlama Linki Gönder'}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Link
                component={RouterLink}
                to="/login"
                underline="hover"
                sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}
              >
                ← Giriş sayfasına dön
              </Link>
            </Box>
          </form>
        )}

        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mt: 3, fontSize: 11 }}>
          © {new Date().getFullYear()} Miksan Plastik · Tüm hakları saklıdır
        </Typography>
      </Card>
    </Box>
  );
}
